import { RefObject, useState } from "react";
import { ReactTyped } from "react-typed";
import DosModal from "../components/DosModal";
import DosLink from "../components/DosLink";

interface Project {
    name: string;
    tagline: string;
    description: string[];
    technologies: string[];
    image: string;
}

const projects: Project[] = [
    {
        name: "stat-that",
        tagline: "Coaching assistant tool",
        image: "/stat-that.png",
        description: [
            "I built StatThat to solve a real problem I had coaching my daughter's girls lacrosse team: keeping track of playing time and lineups by hand during a live game. It's easy to lose track of who's been on the field, who needs a sub, and whether playing time is being distributed fairly across the roster when you're also trying to watch the game. I wanted something I could pull out on the sideline that would let me manage the clock, lineup, and substitutions in real time, and I designed the data model to be sport-agnostic from the start so it could extend beyond lacrosse later.",
            "I already had years of Angular/TypeScript experience, so the frontend was an easy call. For everything else (backend, database, hosting) I deliberately chose tools outside my comfort zone to force myself to learn something new: Supabase for the backend (Postgres, auth, real-time sync, edge functions, row-level security) and Netlify for hosting, both chosen for how quickly I could get something running without provisioning infrastructure myself. I actually started with Terraform to manage the Supabase schema, since that mirrored my Azure IaC background, but once the schema stabilized I dropped it in favor of plain Supabase CLI migrations, a case of recognizing the extra tooling wasn't earning its keep for a project this size.",
            "This was also my first real attempt at building a PWA. I wanted to be able to add it to my home screen and use it like a native app during games. That meant no App Store, it works offline if the sideline has no signal, and it syncs back up automatically once it does. So @angular/service-worker and an installable manifest were part of the plan from day one.",
        ],
        technologies: ["Angular", "Supabase", "Netlify", "PWA"],
    },
    {
        name: "bar4bar",
        tagline: "Godot roguelike game",
        image: "/bar4bar.png",
        description: [
            "I've always wanted to make a game, this is my best attempt at actually doing it. I had previously used Unity with C#, but for this project used the open source engine Godot. Bar4Bar is a battle-rap roguelike deckbuilder I'm building solo in Godot 4 with C#, where you build rhyme patterns across turn-based rounds to win over a crowd, competing against an AI opponent through flow, rhyme schemes, and punchlines.",
            "Rather than using lyrics or rhyming words, I abstracted rhymes into a color system: primary colors act as base rhymes, and secondary colors act as \"slant rhymes\" that can match either of the primary colors used in their makeup. The color system was intended to simplify the gameplay and make for easier localization. Cards carry setup/payoff values so a card played now can prime a bonus that cashes out later, which added a sequencing layer I hadn't originally planned for but found made the deckbuilding decisions a lot more interesting.",
            "I built toward an MVP deliberately: the whole first phase of development was pure gameplay logic. This included card data, draw/discard, rhyme resolution, pattern detection, flow scoring, the crowd meter, a full round loop, and a simulated AI opponent. I also implemented scenes, navigation, and the roguelite meta-layer (node map, card drafts, relics) with basic features for MVP. I did all this with limited placeholder UI, because I wanted to prove the core loop was actually fun before spending any time on menus, art, or polish.",
            "I used this project as a testbed for using Claude as a project manager, not just a coding assistant. I keep a single spec file that Claude reads at the start of every session it documents the architecture, tracks a build-order table I check off step by step, and maintains a running change log of design decisions. That gave me persistent continuity across sessions (Claude always knows exactly what phase I'm in and what's next) and forced me to keep the design doc honest, since it's the same file driving both my planning and the AI's implementation work.",
        ],
        technologies: ["Godot", "C#", "Claude"],
    },
];

function PersonalProjects({passedRef}: {passedRef: RefObject<HTMLDivElement>}) {
    const [openProject, setOpenProject] = useState<Project | null>(null);
    const [showScreenshot, setShowScreenshot] = useState(false);

    return (
        <div id={"personal-projects"} ref={passedRef}  className="container section">
            <div className="row">
                <div className="col-sm-8">
                    <ReactTyped className="header" strings={["Personal Projects"]} cursorChar="_"/>
                    <ul className="dos-list">
                        {projects.map((project) => (
                            <li key={project.name}>
                                <button className="dos-entry" onClick={() => setOpenProject(project)}>
                                    <span className="dos-entry-name">{project.name}</span>
                                    <span className="dos-entry-meta">{project.tagline}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="col-sm-4 image-container">
                    <img src="/lax2025.jpg" alt="Lebanon Lacrosse 2025"/>
                </div>
            </div>
            {openProject && (
                <DosModal title={`C:\\PROJECTS\\${openProject.name.toUpperCase().replace(/\s+/g, "_")}.TXT`}
                          onClose={() => { setOpenProject(null); setShowScreenshot(false); }}>
                    <DosLink onClick={() => setShowScreenshot(true)}>view_screenshot.png</DosLink>
                    <h1>{openProject.name}</h1>
                    <h3>{openProject.tagline}</h3>
                    {openProject.description.map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                    <h4>Relevant Technologies:</h4>
                    <ul>
                        {openProject.technologies.map((tech) => <li key={tech}>{tech}</li>)}
                    </ul>
                </DosModal>
            )}
            {openProject && showScreenshot && (
                <DosModal title={`C:\\PROJECTS\\${openProject.name.toUpperCase().replace(/\s+/g, "_")}\\SCREENSHOT.PNG`}
                          onClose={() => setShowScreenshot(false)}>
                    <img className="dos-modal-image" src={openProject.image} alt={`${openProject.name} screenshot`}/>
                </DosModal>
            )}
        </div>
    );
  }

  export default PersonalProjects;
